import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';

// Strips "TLS-XX-" style agent prefixes off an existing client_code, leaving just the nickname
function stripAgentPrefix(code) {
  if (!code) return '';
  return code.replace(/^\s*TLS\s*[-/]?\s*[0-9]*\s*[-/]?\s*/i, '').trim();
}

export default function ClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role, agentRefId } = useAuth();
  const isEdit = !!id;
  const canEditNickname = role === 'sadmin' || role === 'admin';
  // A limited agent role creating their own client — locked to their own agent id,
  // never a picker, so they can't (even accidentally) create a client under someone
  // else's code.
  const isLimitedAgent = role === 'agent';
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', mobile: '',
    passport_number: '', passport_expiry: '', nationality: '',
    date_of_birth: '', address: '', city: '', country: '', notes: '',
    care_of_agent_id: '',
  });
  const [nickname, setNickname] = useState('');
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const { data: activeAgents } = await supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code');
      let allAgents = activeAgents || [];
      if (isEdit) {
        const { data } = await supabase.from('clients').select('*').eq('id', id).single();
        if (data) {
          setForm({ ...data, care_of_agent_id: data.care_of_agent_id || '' });
          setNickname(stripAgentPrefix(data.client_code));
          // The dropdown only lists active agents, but this client may be assigned to an
          // agent that has since been deactivated — without its option present, the select
          // silently renders "— Select agent —" as if no agent were linked at all.
          if (data.care_of_agent_id && !allAgents.some(a => a.id === data.care_of_agent_id)) {
            const { data: inactive } = await supabase.from('agents').select('id, tls_code, full_name').eq('id', data.care_of_agent_id).single();
            if (inactive) allAgents = [...allAgents, { ...inactive, full_name: `${inactive.full_name} (inactive)` }];
          }
        }
      }
      setAgents(allAgents);
      if (!isEdit && isLimitedAgent && agentRefId) set('care_of_agent_id', agentRefId);
    })();
  }, [id, isEdit, isLimitedAgent, agentRefId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedAgent = agents.find(a => a.id === form.care_of_agent_id);
  const codePreview = selectedAgent && nickname.trim()
    ? `${selectedAgent.tls_code}-${nickname.trim().toUpperCase()}`
    : selectedAgent ? selectedAgent.tls_code : '—';

  const handleSubmit = async () => {
    if (!form.full_name) { setError('Full name is required'); return; }
    if (!isEdit) {
      if (!form.care_of_agent_id) { setError('Agent is required'); return; }
    }
    setSaving(true); setError('');
    try {
      const username = user?.email?.split('@')[0] || 'unknown';
      const clean = {
        ...form,
        care_of_agent_id: form.care_of_agent_id || null,
        passport_expiry: form.passport_expiry || null,
        date_of_birth: form.date_of_birth || null,
      };
      if (isEdit) {
        const update = { ...clean, updated_by: username, updated_at: new Date().toISOString() };
        // Nickname (client_code) can only be changed by sadmin/admin, and only when re-linked to an agent.
        // No nickname just means the client bills directly under the agent's bare code.
        if (canEditNickname && selectedAgent) {
          update.client_code = nickname.trim() ? `${selectedAgent.tls_code}-${nickname.trim().toUpperCase()}` : selectedAgent.tls_code;
        } else {
          delete update.client_code;
        }
        const { error: err } = await supabase.from('clients').update(update).eq('id', id);
        if (err) throw err;
        navigate(`/clients/${id}`);
      } else {
        const clientCode = nickname.trim() ? `${selectedAgent.tls_code}-${nickname.trim().toUpperCase()}` : selectedAgent.tls_code;
        const { data, error: err } = await supabase.from('clients').insert({ ...clean, client_code: clientCode, created_by: username }).select().single();
        if (err) throw err;
        // A limited agent can't see /clients/:id (admin-only) — send them back to their
        // own portal, where the new client now shows up in their Clients tab.
        navigate(isLimitedAgent ? '/agent' : `/clients/${data.id}`);
      }
    } catch (err) {
      if (err.code === '23505' || /duplicate key/i.test(err.message || '')) {
        setError(`Nickname "${nickname}" is already used under this agent. Pick a different nickname.`);
      } else {
        setError(err.message || 'Error saving');
      }
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">{isEdit ? 'Edit Client' : 'New Client'}</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Client'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 16 }}>{error}</div>}

      <div className="card" style={{ marginBottom: 16, background: 'rgba(240,112,64,0.04)', border: '1px solid rgba(240,112,64,0.25)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary, #f07040)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
          Client Identity (Internal — used for accounts/ledgers)
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Care of Agent *</label>
            {isLimitedAgent ? (
              <div className="form-input" style={{ background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)' }}>
                {selectedAgent ? `${selectedAgent.tls_code} — ${selectedAgent.full_name}` : '—'} (locked to your own agent code)
              </div>
            ) : (
              <select className="form-input" value={form.care_of_agent_id || ''} onChange={e => set('care_of_agent_id', e.target.value || null)} disabled={isEdit && !canEditNickname}>
                <option value="">— Select agent —</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.tls_code} — {a.full_name}</option>)}
              </select>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Nickname (optional)</label>
            <input
              className="form-input"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder="e.g. AKEEL, LTO, BULSHAN — leave blank to bill directly under the agent code"
              disabled={isEdit && !canEditNickname}
              style={isEdit && !canEditNickname ? { background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)' } : {}}
            />
            {isEdit && !canEditNickname && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Locked — only sadmin/admin can rename</div>
            )}
          </div>
          <div className="form-group full-width">
            <label className="form-label">Client Code Preview</label>
            <div className="form-input" style={{ fontFamily: 'monospace', fontWeight: 700, background: 'var(--bg-secondary, #f5f5f5)' }}>{codePreview}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="form-grid">
          {[['full_name','Full Name * (shown on invoices, pro forma, quotations)'],['email','Email'],['phone','Phone'],['mobile','Mobile'],['passport_number','Passport Number'],['passport_expiry','Passport Expiry','date'],['nationality','Nationality'],['date_of_birth','Date of Birth','date'],['city','City'],['country','Country']].map(([k, label, type]) => (
            <div className="form-group" key={k}>
              <label className="form-label">{label}</label>
              <input className="form-input" type={type || 'text'} value={form[k] || ''} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
          <div className="form-group full-width">
            <label className="form-label">Address</label>
            <input className="form-input" value={form.address || ''} onChange={e => set('address', e.target.value)} />
          </div>
          <div className="form-group full-width">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}
